/*
 * PageWright — native search/collection click-and-search analytics (Phase 2A, 2026-07-20).
 *
 * Covers the two native, server-rendered surfaces pw-image-search.js's own beacons don't reach:
 *   - /search            (search-results.liquid)  -> 'search' / 'zero_results' / 'view_item_list'
 *   - /collections/*     (main-collection.liquid, pw-collaborations.liquid, pw-projects.liquid,
 *                          etc.)                   -> 'view_item_list' only (no query to report)
 *
 * Posts to the SAME public beacon (@pagewright/api's /api/collect -> Neon cs_analytics) pw-
 * image-search.js already uses, via the same fire-and-forget navigator.sendBeacon pattern (that
 * endpoint's own design intent).
 *
 * NOT a pure no-op off search/collection routes any more (corrected 2026-08-30 — the header used
 * to claim it was): `page_view` (CS-0642) fires site-wide, unconditionally, on every route. Every
 * OTHER listener below still gates on the current route.
 *
 * CS-0623 (2026-08-30): facets.js dispatches the standard `shopify:search:update` event
 * (StandardEvents.searchUpdate) on every facet/sort/pagination re-render on /search — nothing
 * used to re-fire the search/zero_results/view_item_list beacons for those, so filtered search
 * views were invisible. A document-level listener now re-fires them. SCOPING IS LOAD-BEARING:
 * assets/predictive-search.js dispatches the *same* event type on every typeahead debounce (the
 * search modal), and both bubble to `document` — an unscoped listener would emit a 'search'
 * beacon per keystroke and corrupt the very dashboards this file feeds. The listener below checks
 * `event.target` is a `<facets-form-component>` (assets/facets.js, tag `facets-form-component`),
 * never a `<predictive-search-component>` (assets/predictive-search.js).
 *
 * Deliberately does NOT touch snippets/product-card.liquid (shared across many non-search/
 * collection surfaces — home page, upsells, etc. — so a shared-file edit would be higher risk
 * for no benefit here). Instead the product handle is derived client-side from the `href` of the
 * existing `.product-card__link` anchor every real product card already renders.
 */
(function () {
  'use strict';

  // PageWright's production backend. This asset has no per-page settings slot to swap via
  // scripts/point-templates.sh (that script only rewrites template JSON block settings) — for a
  // one-off staging check, temporarily hand-edit this constant and discard the diff before
  // committing/pushing, same convention that script's own header documents for template swaps.
  var BACKEND_URL = (window.PW_BACKEND_URL || '')  // set once by layout/theme.liquid from the
  // per-theme `backend_url` setting. Blank DISABLES backend calls rather than falling back
  // to production — a staging theme silently writing to prod data is the bug this replaces.

  var PW_SESSION_KEY = 'pw_session_id';

  function pwSessionId() {
    try {
      var id = sessionStorage.getItem(PW_SESSION_KEY);
      if (!id) {
        id = 'pw_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 10);
        sessionStorage.setItem(PW_SESSION_KEY, id);
      }
      return id;
    } catch (e) {
      return undefined;
    }
  }

  function pwGaClientId() {
    var m = /(?:^|;\s*)_ga=GA1\.\d\.(\d+\.\d+)/.exec(document.cookie || '');
    return m ? m[1] : undefined;
  }

  function postCollectEvent(payload) {
    try {
      // Corrected 2026-08-30 (independent-audit finding): a blank BACKEND_URL used to mean
      // "POST to a relative /api/collect" (a guaranteed Shopify 404 on the storefront), NOT
      // silence. Blank must mean send nothing — a staging theme with backend_url unset should
      // never even attempt the request, let alone every page load once page_view is un-gated.
      if (!BACKEND_URL) return;
      var body = JSON.stringify(Object.assign(
        { eventVersion: 1, sessionId: pwSessionId(), clientId: pwGaClientId() },
        payload
      ));
      var url = BACKEND_URL + '/api/collect';
      if (navigator.sendBeacon) {
        navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
      } else {
        fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: body, keepalive: true });
      }
    } catch (e) {
      /* analytics beacon must never break the page */
    }
  }

  function productHandleFromHref(href) {
    if (!href) return undefined;
    var m = /\/products\/([^/?#]+)/.exec(href);
    return m ? decodeURIComponent(m[1]) : undefined;
  }

  function isSearchRoute() {
    return location.pathname === '/search';
  }

  function isCollectionRoute() {
    return location.pathname.indexOf('/collections/') === 0;
  }

  // Only real, rendered cards — matches the same selector pw-image-search.js's applySearchAzSort
  // already relies on for "how many products are actually on screen" (data-product-id, no
  // onboarding/placeholder card).
  function countRealProductCards() {
    return document.querySelectorAll('product-card[data-product-id]:not([data-placeholder])').length;
  }

  /**
   * The search/collection view-event trio, shared by the initial DOMContentLoaded load path and
   * the shopify:search:update re-render path (CS-0623). `searching` distinguishes /search (which
   * reports a query + can zero-result) from a plain /collections/* browse (view_item_list only).
   */
  function fireSearchBeacons(searching, query, count) {
    if (searching && query && count === 0) {
      postCollectEvent({ event: 'zero_results', query: query });
      return;
    }
    if (searching && query) {
      postCollectEvent({ event: 'search', query: query, resultCount: count });
    }
    postCollectEvent(query ? { event: 'view_item_list', query: query, resultCount: count }
                           : { event: 'view_item_list', resultCount: count });
  }

  function fireLoadEvents() {
    var searching = isSearchRoute();
    var browsing = isCollectionRoute();
    if (!searching && !browsing) return;

    var query = searching ? (new URLSearchParams(location.search).get('q') || '').trim() : '';
    fireSearchBeacons(searching, query, countRealProductCards());
  }

  // CS-0623 (2026-08-30): re-fire the same beacon trio when facets.js re-renders the results —
  // otherwise only the FIRST page load of /search or /collections/* is ever measured, and every
  // filtered/sorted view is invisible. Document-level because facets.js dispatches from deep in
  // the results section and the event bubbles (the same pattern facets.js/paginated-list.js use
  // for their own document-level listeners).
  document.addEventListener('shopify:search:update', function (e) {
    // SCOPE GUARD, load-bearing: predictive-search.js dispatches this identical event type on
    // every typeahead debounce (the search modal, from a <predictive-search-component>). Only a
    // facets/sort re-render on the /search RESULTS page (<facets-form-component>) is a real
    // "the shopper looked at a filtered result set" signal — a modal keystroke is not a view.
    if (!e.target || !e.target.tagName || e.target.tagName.toUpperCase() !== 'FACETS-FORM-COMPONENT') return;
    if (!isSearchRoute()) return;

    var query = (e.search && e.search.query ? e.search.query : '').trim();
    // e.promise resolves to {totalCount} once facets.js's section re-render lands — reading the
    // DOM before then would count the STALE pre-filter cards. Fall back to a DOM count only if
    // the promise itself is unavailable (defensive; the standard event always sets it today).
    if (e.promise && typeof e.promise.then === 'function') {
      e.promise
        .then(function (result) {
          var count = result && typeof result.totalCount === 'number' ? result.totalCount : countRealProductCards();
          fireSearchBeacons(true, query, count);
        })
        .catch(function () { /* analytics beacon must never break the page */ });
    } else {
      fireSearchBeacons(true, query, countRealProductCards());
    }
  });

  document.addEventListener('click', function (e) {
    if (!isSearchRoute() && !isCollectionRoute()) return;
    var link = e.target.closest('.product-card__link');
    if (!link) return;
    var handle = productHandleFromHref(link.getAttribute('href'));
    if (handle) postCollectEvent({ event: 'select_item', handle: handle });
  });

  // Phase 2B (2026-07-28): begin_checkout. Reachable from the cart page/drawer on ANY route
  // (not gated to search/collection like the listener above) -- the native checkout submit
  // button (snippets/cart-summary.liquid's #checkout, name="checkout") posts the cart form
  // directly to Shopify's checkout with no client-side navigation intercept, so this fires via
  // sendBeacon specifically because it must complete before the page unloads. No handle -- a
  // cart can have multiple lines, begin_checkout is a cart-level signal, not tied to one product
  // (matches the contract's optional handle field).
  document.addEventListener('click', function (e) {
    if (e.target.closest('[name="checkout"]')) {
      postCollectEvent({ event: 'begin_checkout' });
    }
  });

  /*
   * generate_lead for the NATIVE Shopify newsletter forms (2026-07-29).
   *
   * Why: Google Ads' "Submit lead form" conversion action was triggering on a Google Tag
   * page-load rule — "visits a page starting with /pages" — i.e. counting every /pages/...
   * PAGEVIEW as a lead. Useless for bidding, and actively harmful if set Primary.
   *
   * The freebie-claim form (what both Winter2026 ads point at) already fires generate_lead
   * correctly from inside its success branch — see assets/pw-freebie-claim.js. The gap was the
   * two native {% form 'customer' %} newsletter signups (home band + footer), which emitted
   * nothing. Those are full-page POSTs: Shopify redirects back with ?customer_posted=true.
   * Firing the same event here means all three lead surfaces emit ONE consistent signal, and
   * the Ads conversion can be scoped to the event instead of a URL pattern.
   *
   * Chose an event over a dedicated /pages/thank-you-leads URL deliberately: a thank-you page
   * can be reached by direct navigation, refresh, back-button or a shared link, every one of
   * which inflates conversions — the same over-counting problem we are removing.
   *
   * The sessionStorage guard matters for the same reason: ?customer_posted=true SURVIVES a
   * refresh, so an unguarded handler would re-fire on every reload of that URL.
   */
  /*
   * GA4 lead-event value/currency (tasks.db #780 / CS-0095, 2026-09-10) — mirror of the same
   * constants in assets/pw-freebie-claim.js.
   *
   * An April 2026 GA4 validation change made BOTH `value` and `currency` mandatory for a
   * `generate_lead` event to qualify as an Ads-importable key event; previously optional. GA4
   * reports nothing when they are missing — it still stars the event as a key event — while the
   * Google Ads import wizard silently omits it. Neither of this theme's two generate_lead call
   * sites carried either parameter, which is the whole of #780.
   *
   * LEAD_VALUE IS A PLACEHOLDER — PENDING PAUL'S DECISION. Paul has never set a lead value.
   * Import eligibility needs only a present, numeric `value`; the magnitude matters solely under
   * value-based bidding (tROAS / maximize conversion value), at which point this nominal 1 must
   * be replaced with a real figure. Not a considered number.
   *
   * LEAD_CURRENCY must stay identical to pw-freebie-claim.js's: Google Ads imports ONE conversion
   * action across all three lead surfaces, so a disagreement here feeds it a mixed-currency
   * stream. tests/test_ga4_generate_lead_value_currency_091000.py pins the two files in lockstep.
   */
  var LEAD_VALUE = 1;
  var LEAD_CURRENCY = 'USD';

  function fireNewsletterLead() {
    var params = new URLSearchParams(window.location.search);
    if (params.get('customer_posted') !== 'true') return;

    // One event per submission, not per page view of the redirected URL.
    var key = 'pw_lead_fired:' + window.location.pathname + window.location.search;
    try {
      if (sessionStorage.getItem(key)) return;
    } catch (e) {
      /* private mode / storage disabled → fall through and fire once per load rather than never */
    }

    // #1722: through the gate -- withheld on staging/preview themes; also stays silent when gtag
    // is absent (ad-blocked / Google app missing), as before. See assets/pw-analytics-gate.js.
    // The dedupe key is written only once the event was actually SENT (3-model review fix): a lead
    // withheld on staging, or one that met no gtag, must not burn the key for the session.
    if (!window.PWAnalyticsGate) return;
    var sent = window.PWAnalyticsGate.gtagEvent('generate_lead', {
      method: 'newsletter',
      form_source: 'shopify_customer_form',
      page_path: window.location.pathname,
      // See LEAD_VALUE / LEAD_CURRENCY above — GA4-mandated since April 2026, placeholder value.
      value: LEAD_VALUE,
      currency: LEAD_CURRENCY,
    });
    if (sent) {
      try {
        sessionStorage.setItem(key, '1');
      } catch (e) {
        /* storage disabled → fires once per load rather than never */
      }
    }
  }

  /**
   * CS-0642 (2026-08-30): site-wide page_view beacon — the foundational analytics gap. Unlike
   * every other event in this file, this is NOT gated behind isSearchRoute()/isCollectionRoute():
   * page_view is a baseline event every route needs, not a search/collection-only signal.
   *
   * `path` requires batmanspaz/pagewright PR #310 (AnalyticsRecordSchema + cs_analytics.path
   * column) to persist meaningfully — before that PR the row would be accepted (202) but written
   * with every dimension null, per the independent-audit finding that reshaped this fix. Do not
   * ship this ahead of that PR landing.
   *
   * Deliberately does NOT also emit a gtag('event','page_view') — the Shopify Google & YouTube
   * app injects gtag and, per pw-freebie-claim.js's header comment, typically auto-sends its own
   * GA4 page_view; adding a second one here risks double-counting every pageview in GA4 and
   * corrupting Ads bidding data. Verify in-browser (network tab, en=page_view on a
   * google-analytics.com/g/collect request) before ever adding that half.
   */
  function firePageView() {
    postCollectEvent({ event: 'page_view', path: location.pathname });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      fireLoadEvents();
      fireNewsletterLead();
      firePageView();
    });
  } else {
    fireLoadEvents();
    fireNewsletterLead();
    firePageView();
  }
})();
